import { useEffect, useState } from "react";
import { Alert, Skeleton, Tooltip, theme } from "antd";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ApiService } from "../../api";
import type { ProcessAnalytics } from "../../api";
import { Panel } from "../../components/surfaces";
import { space } from "../../theme/space";
import { BarRow, Chip, EmptyState, PanelHeading } from "./parts";

/** The one bar colour on this page, validated against the white card at ≥3:1.
 *  Shading bars by their own value would double-encode length as hue. */
const BAR = "#2a78d6";

/** Hours as something a person says out loud.
 *
 *  "37.4 hours" is a measurement; "a day and a half" is an answer. Precision
 *  past the first significant unit is noise here — nobody acts differently on
 *  37.4 than on 38. */
export const speak = (hours: number | null | undefined, t: TFunction): string => {
  if (hours === null || hours === undefined) return t("—");
  if (hours < 1) return t("under an hour");
  if (hours < 48) {
    const rounded = Math.round(hours);
    return rounded === 1 ? t("1 hour") : t("{{count}} hours", { count: rounded });
  }
  const days = Math.round(hours / 24);
  return days === 1 ? t("1 day") : t("{{count}} days", { count: days });
};

type Props = {
  dateFrom?: string;
  dateTo?: string;
  template?: number;
  /** True when the page is filtered to one process, so a row need not repeat
   *  which one it belongs to. */
  singleProcess: boolean;
};

/**
 * "Where is this process slow, and who is slow in it?"
 *
 * The most common complaint about any approval system, and until now this one
 * could not answer it. Every timestamp was already being recorded; nothing read
 * them that way.
 *
 * Two lists, deliberately apart. **Steps** are a property of the process
 * design — a step that takes four days takes four days whoever is on it.
 * **Approvers** are a property of people, which is the more sensitive number,
 * so it never appears without how much that person is carrying beside it.
 *
 * The headline is a **median**, not an average. One case that sat over a
 * shutdown drags a mean into uselessness, and the mean is the number that gets
 * quoted in a meeting. The average is shown beside it, labelled, so the gap
 * between them is visible rather than hidden by the choice.
 */
const ProcessSpeed = ({ dateFrom, dateTo, template, singleProcess }: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [data, setData] = useState<ProcessAnalytics | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ApiService.apiMainAnalyticsProcessRetrieve(dateFrom, dateTo, template)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError("");
        }
      })
      .catch(() => {
        // A failed load says so. The alternative this page used to have —
        // rendering an empty band — reads as "nothing is slow", which is a
        // claim, and the wrong one.
        if (!cancelled) setError(t("This could not be worked out just now."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, template, t]);

  const measured = (data?.steps ?? []).filter((s) => s.median_hours !== null);
  const slowest = Math.max(1, ...measured.map((s) => s.median_hours ?? 0));
  const busiest = Math.max(1, ...(data?.approvers ?? []).map((a) => a.pending));

  return (
    <Panel padding={space.wide} style={{ marginBottom: space.base }}>
      <PanelHeading
        title={t("Where is it slow?")}
        hint={t(
          "How long cases sit in each state, measured from when they moved on"
        )}
      />

      {error ? (
        <Alert type="warning" showIcon message={error} />
      ) : loading && !data ? (
        <Skeleton active paragraph={{ rows: 5 }} title={false} />
      ) : measured.length === 0 ? (
        <EmptyState
          title={t("Not enough finished work to measure")}
          hint={t(
            "A step is measured when a case leaves it. Widen the date range, or come back once some cases have moved on."
          )}
        />
      ) : (
        <>
          {data?.bottleneck && (
            <div
              style={{
                marginBottom: space.base,
                fontSize: 15,
                color: token.colorText,
              }}
            >
              {t(
                "The slowest step is {{step}}, where cases wait {{time}} before moving on.",
                {
                  step: data.bottleneck.status_name,
                  time: speak(data.bottleneck.median_hours, t),
                }
              )}{" "}
              <span style={{ color: token.colorTextTertiary, fontSize: 13 }}>
                {t("Measured across {{count}} cases.", { count: data.sample })}
              </span>
            </div>
          )}

          <div>
            {measured.map((step, index) => (
              <div
                key={`${step.template_id}-${step.status_name}`}
                style={
                  index > 0
                    ? { borderTop: `1px solid ${token.colorSplit}` }
                    : undefined
                }
              >
                <BarRow
                  share={(step.median_hours ?? 0) / slowest}
                  color={BAR}
                  label={
                    <>
                      {step.status_name}
                      {!singleProcess && step.template && (
                        <>
                          {" "}
                          <Chip>{step.template.short_name}</Chip>
                        </>
                      )}
                    </>
                  }
                  sublabel={
                    <>
                      {t("{{count}} finished · average {{average}}", {
                        count: step.count,
                        average: speak(step.avg_hours, t),
                      })}
                      {step.still_waiting > 0 && (
                        <>
                          {" · "}
                          {/* Never folded into the median: a wait in progress
                              has no duration yet, and counting it as zero would
                              make the slowest step look like the fastest. */}
                          {t("{{count}} still waiting", {
                            count: step.still_waiting,
                          })}
                        </>
                      )}
                    </>
                  }
                  value={speak(step.median_hours, t)}
                  meta={t("typical")}
                />
              </div>
            ))}
          </div>

          {(data?.unattributed ?? 0) > 0 && (
            <div
              style={{
                marginTop: space.snug,
                fontSize: 12,
                color: token.colorTextTertiary,
              }}
            >
              {/* Said out loud rather than quietly under-reported. */}
              {t(
                "{{count}} recorded moves name a state that no longer exists — usually a rename — and are left out.",
                { count: data!.unattributed }
              )}
            </div>
          )}

          {(data?.approvers ?? []).length > 0 && (
            <div style={{ marginTop: space.wide }}>
              <PanelHeading
                title={t("Who is holding work")}
                hint={t(
                  "Slots waiting on each person, and how long they usually take to answer"
                )}
              />
              {data!.approvers.map((person, index) => (
                <div
                  key={person.user_id}
                  style={
                    index > 0
                      ? { borderTop: `1px solid ${token.colorSplit}` }
                      : undefined
                  }
                >
                  <BarRow
                    share={person.pending / busiest}
                    color={BAR}
                    label={person.name}
                    sublabel={
                      person.median_hours === null
                        ? t("No answered signatures in this period")
                        : t("Usually answers in {{time}} · {{count}} answered", {
                            time: speak(person.median_hours, t),
                            count: person.count,
                          })
                    }
                    value={person.pending}
                    meta={t("waiting")}
                  />
                </div>
              ))}
              {(data?.estimated_responses ?? 0) > 0 && (
                <Tooltip
                  title={t(
                    "Signatures given before the system recorded when somebody was asked. The gap is worked out from the audit trail instead."
                  )}
                >
                  <div
                    style={{
                      marginTop: space.snug,
                      fontSize: 12,
                      color: token.colorTextTertiary,
                      borderBottom: "1px dotted",
                      display: "inline-block",
                    }}
                  >
                    {t("{{count}} of these times are estimated, not measured.", {
                      count: data!.estimated_responses,
                    })}
                  </div>
                </Tooltip>
              )}
            </div>
          )}
        </>
      )}
    </Panel>
  );
};

export default ProcessSpeed;
